import { useEffect, useRef, useState, useCallback } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { X, Brush, Eraser, Wand2, FlipHorizontal2, RotateCcw, Check, Loader2, Scissors, Stamp } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);
const MAXW = 1400;

function loadImg(src) {
  return new Promise((resolve) => {
    const im = new Image(); im.crossOrigin = "anonymous";
    im.onload = () => resolve(im); im.onerror = () => resolve(null); im.src = src;
  });
}

// Deterministic manual masking + OpenCV auto-mask / logo removal. NO AI.
export default function V5MaskEditor({ originalPath, onApply, onClose }) {
  const [mode, setMode] = useState("cutout"); // cutout | logo
  const [tool, setTool] = useState("erase");   // cutout: keep|erase ; logo: paint|unpaint
  const [brush, setBrush] = useState(60);
  const [feather, setFeather] = useState(2);
  const [busy, setBusy] = useState("");
  const [dims, setDims] = useState([0, 0]);
  const [curPath, setCurPath] = useState(originalPath); // becomes inpainted path after logo removal

  const baseRef = useRef(null);   // HTMLImageElement of current working image
  const viewRef = useRef(null);   // visible canvas
  const keepRef = useRef(null);   // offscreen: RGBA keep-alpha mask (opaque = keep)
  const logoRef = useRef(null);   // offscreen: RGBA red paint = inpaint region
  const drawing = useRef(false);

  const redraw = useCallback(() => {
    const v = viewRef.current, base = baseRef.current;
    if (!v || !base) return;
    const [W, H] = [v.width, v.height];
    const ctx = v.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    // checker bg to show transparency
    const s = 24; for (let y = 0; y < H; y += s) for (let x = 0; x < W; x += s) { ctx.fillStyle = ((x / s + y / s) % 2 === 0) ? "#2a2a2e" : "#1c1c1f"; ctx.fillRect(x, y, s, s); }
    // composited cutout preview
    const tmp = document.createElement("canvas"); tmp.width = W; tmp.height = H;
    const tc = tmp.getContext("2d");
    tc.drawImage(base, 0, 0, W, H);
    tc.globalCompositeOperation = "destination-in";
    if (feather > 0) tc.filter = `blur(${feather}px)`;
    tc.drawImage(keepRef.current, 0, 0);
    tc.filter = "none"; tc.globalCompositeOperation = "source-over";
    ctx.drawImage(tmp, 0, 0);
    // logo paint overlay (only in logo mode)
    if (mode === "logo") { ctx.globalAlpha = 0.5; ctx.drawImage(logoRef.current, 0, 0); ctx.globalAlpha = 1; }
  }, [feather, mode]);

  const init = useCallback(async () => {
    setBusy("load");
    const im = await loadImg(abs(curPath));
    if (!im) { toast.error("Could not load image"); setBusy(""); return; }
    let W = im.naturalWidth, H = im.naturalHeight;
    if (W > MAXW) { H = Math.round(H * MAXW / W); W = MAXW; }
    baseRef.current = im; setDims([W, H]);
    for (const r of [viewRef, keepRef, logoRef]) { r.current.width = W; r.current.height = H; }
    // default: keep everything (full opaque white) so nothing is cut until user erases / auto
    const kc = keepRef.current.getContext("2d");
    kc.clearRect(0, 0, W, H); kc.fillStyle = "#fff"; kc.fillRect(0, 0, W, H);
    logoRef.current.getContext("2d").clearRect(0, 0, W, H);
    setBusy(""); setTimeout(redraw, 30);
  }, [curPath, redraw]);

  useEffect(() => { init(); }, [init]);
  useEffect(() => { redraw(); }, [redraw]);

  const ptToImg = (e) => {
    const v = viewRef.current, rect = v.getBoundingClientRect();
    return [(e.clientX - rect.left) / rect.width * v.width, (e.clientY - rect.top) / rect.height * v.height];
  };
  const paintAt = (x, y) => {
    if (mode === "cutout") {
      const kc = keepRef.current.getContext("2d");
      kc.globalCompositeOperation = tool === "keep" ? "source-over" : "destination-out";
      kc.fillStyle = "#fff"; kc.beginPath(); kc.arc(x, y, brush / 2, 0, Math.PI * 2); kc.fill();
      kc.globalCompositeOperation = "source-over";
    } else {
      const lc = logoRef.current.getContext("2d");
      lc.globalCompositeOperation = tool === "unpaint" ? "destination-out" : "source-over";
      lc.fillStyle = "rgba(239,68,68,1)"; lc.beginPath(); lc.arc(x, y, brush / 2, 0, Math.PI * 2); lc.fill();
      lc.globalCompositeOperation = "source-over";
    }
    redraw();
  };
  const down = (e) => { drawing.current = true; const [x, y] = ptToImg(e); paintAt(x, y); };
  const movePaint = (e) => { if (!drawing.current) return; const [x, y] = ptToImg(e); paintAt(x, y); };
  const up = () => { drawing.current = false; };

  const autoMask = async (method) => {
    setBusy("auto");
    try {
      const r = await api.v5AutoMask({ path: curPath, method });
      const mk = await loadImg(r.mask_data_url);
      const [W, H] = dims; const kc = keepRef.current.getContext("2d");
      // convert grayscale mask -> alpha (white=keep opaque)
      const t = document.createElement("canvas"); t.width = W; t.height = H;
      const tc = t.getContext("2d"); tc.drawImage(mk, 0, 0, W, H);
      const id = tc.getImageData(0, 0, W, H); const d = id.data;
      for (let i = 0; i < d.length; i += 4) { const lum = d[i]; d[i] = d[i + 1] = d[i + 2] = 255; d[i + 3] = lum; }
      tc.putImageData(id, 0, 0);
      kc.clearRect(0, 0, W, H); kc.drawImage(t, 0, 0);
      redraw(); toast.success(`Auto ${method} mask applied — refine with the brush`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto mask failed"); }
    setBusy("");
  };

  const invert = () => {
    const [W, H] = dims; const kc = keepRef.current.getContext("2d");
    const id = kc.getImageData(0, 0, W, H); const d = id.data;
    for (let i = 0; i < d.length; i += 4) { d[i] = d[i + 1] = d[i + 2] = 255; d[i + 3] = 255 - d[i + 3]; }
    kc.putImageData(id, 0, 0); redraw();
  };
  const resetMask = () => {
    const [W, H] = dims; const kc = keepRef.current.getContext("2d");
    kc.clearRect(0, 0, W, H); kc.fillStyle = "#fff"; kc.fillRect(0, 0, W, H);
    logoRef.current.getContext("2d").clearRect(0, 0, W, H); redraw();
  };

  const removeLogos = async () => {
    // export logo paint as grayscale mask (red -> 255)
    const [W, H] = dims;
    const t = document.createElement("canvas"); t.width = W; t.height = H;
    const tc = t.getContext("2d"); const src = logoRef.current.getContext("2d").getImageData(0, 0, W, H);
    const out = tc.createImageData(W, H); const s = src.data, o = out.data;
    let any = false;
    for (let i = 0; i < s.length; i += 4) { const on = s[i + 3] > 10; if (on) any = true; o[i] = o[i + 1] = o[i + 2] = on ? 255 : 0; o[i + 3] = 255; }
    if (!any) { toast.error("Paint over the logo/sponsor first"); return; }
    tc.putImageData(out, 0, 0);
    setBusy("logo");
    try {
      const r = await api.v5Inpaint({ path: curPath, mask_data_url: t.toDataURL("image/png"), radius: 6, method: "telea" });
      setCurPath(r.path); // reload with cleaned base (keep mask preserved via re-init? re-init resets keep) 
      toast.success("Logos removed (deterministic inpainting)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Logo removal failed"); }
    setBusy("");
  };

  const apply = async () => {
    const [W, H] = dims; const base = baseRef.current;
    const f = document.createElement("canvas"); f.width = W; f.height = H;
    const fc = f.getContext("2d");
    fc.drawImage(base, 0, 0, W, H);
    fc.globalCompositeOperation = "destination-in";
    if (feather > 0) fc.filter = `blur(${feather}px)`;
    fc.drawImage(keepRef.current, 0, 0);
    fc.filter = "none"; fc.globalCompositeOperation = "source-over";
    setBusy("apply");
    try {
      const blob = await new Promise((res) => f.toBlob(res, "image/png"));
      const file = new File([blob], "masked.png", { type: "image/png" });
      const r = await api.igUpload(file, "masked");
      onApply({ maskedPath: r.path });
      toast.success("Mask applied — subject cut out (no AI used)");
    } catch (e) { toast.error("Could not apply mask"); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4" data-testid="v5-mask-editor">
      <div className="relative flex max-h-[92vh] w-full max-w-[1200px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D0E] text-white">
        <WindowSearch />
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-bold"><Scissors size={16} className="text-[#00F0FF]" /> Mask &amp; Cut out <span className="rounded bg-white/10 px-2 py-0.5 text-[11px] text-[#A1A1AA]">deterministic · no AI</span></div>
          <button onClick={onClose} data-testid="v5-mask-close" className="rounded-lg p-1.5 hover:bg-white/10"><X size={18} /></button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-4 py-2 text-xs">
          <div className="flex overflow-hidden rounded-lg border border-white/15">
            <button onClick={() => { setMode("cutout"); setTool("erase"); }} data-testid="v5-mask-mode-cutout" className={`px-3 py-1.5 font-semibold ${mode === "cutout" ? "bg-[#00F0FF] text-black" : ""}`}>Cut out</button>
            <button onClick={() => { setMode("logo"); setTool("paint"); }} data-testid="v5-mask-mode-logo" className={`px-3 py-1.5 font-semibold ${mode === "logo" ? "bg-[#00F0FF] text-black" : ""}`}>Remove logo</button>
          </div>
          {mode === "cutout" ? (
            <>
              <button onClick={() => setTool("erase")} data-testid="v5-tool-erase" className={`flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 ${tool === "erase" ? "bg-white/15" : ""}`}><Eraser size={13} /> Remove bg</button>
              <button onClick={() => setTool("keep")} data-testid="v5-tool-keep" className={`flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 ${tool === "keep" ? "bg-white/15" : ""}`}><Brush size={13} /> Restore</button>
              <button onClick={() => autoMask("grabcut")} disabled={busy === "auto"} data-testid="v5-auto-grabcut" className="flex items-center gap-1 rounded-lg border border-[#00F0FF]/40 px-2.5 py-1.5 text-[#00F0FF]"><Wand2 size={13} /> Auto (subject)</button>
              <button onClick={() => autoMask("color")} disabled={busy === "auto"} data-testid="v5-auto-color" className="flex items-center gap-1 rounded-lg border border-[#00F0FF]/40 px-2.5 py-1.5 text-[#00F0FF]"><Wand2 size={13} /> Auto (solid bg)</button>
              <button onClick={invert} data-testid="v5-invert" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5"><FlipHorizontal2 size={13} /> Invert</button>
            </>
          ) : (
            <>
              <button onClick={() => setTool("paint")} data-testid="v5-tool-paint" className={`flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 ${tool === "paint" ? "bg-white/15" : ""}`}><Brush size={13} /> Mark logo</button>
              <button onClick={() => setTool("unpaint")} data-testid="v5-tool-unpaint" className={`flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 ${tool === "unpaint" ? "bg-white/15" : ""}`}><Eraser size={13} /> Unmark</button>
              <button onClick={removeLogos} disabled={busy === "logo"} data-testid="v5-remove-logos" className="flex items-center gap-1 rounded-lg bg-[#ef4444] px-2.5 py-1.5 font-semibold text-white">{busy === "logo" ? <Loader2 size={13} className="animate-spin" /> : <Stamp size={13} />} Remove marked</button>
            </>
          )}
          <button onClick={resetMask} data-testid="v5-mask-reset" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5"><RotateCcw size={13} /> Reset</button>
          <label className="flex items-center gap-1">Brush <input type="range" min={8} max={200} value={brush} onChange={(e) => setBrush(+e.target.value)} data-testid="v5-brush-size" className="accent-[#00F0FF]" /></label>
          {mode === "cutout" && <label className="flex items-center gap-1">Feather <input type="range" min={0} max={12} value={feather} onChange={(e) => setFeather(+e.target.value)} data-testid="v5-feather" className="accent-[#00F0FF]" /></label>}
        </div>

        <div className="flex flex-1 items-center justify-center overflow-auto bg-[#111] p-4">
          {busy === "load" ? <Loader2 className="animate-spin text-[#00F0FF]" /> : (
            <canvas ref={viewRef} onMouseDown={down} onMouseMove={movePaint} onMouseUp={up} onMouseLeave={up}
              data-testid="v5-mask-canvas" className="max-h-[62vh] max-w-full cursor-crosshair rounded-lg shadow-2xl" style={{ imageRendering: "auto" }} />
          )}
          <canvas ref={keepRef} className="hidden" /><canvas ref={logoRef} className="hidden" />
        </div>

        <div className="flex items-center justify-between border-t border-white/10 px-4 py-3">
          <p className="text-[11px] text-[#71717A]">Original upload is never modified. Cut-outs use classical OpenCV — zero AI, zero credits.</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-white/15 px-4 py-2 text-sm">Cancel</button>
            <button onClick={apply} disabled={busy === "apply"} data-testid="v5-mask-apply" className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black">{busy === "apply" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Apply to layer</button>
          </div>
        </div>
      </div>
    </div>
  );
}
